import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { colors, fontSizes, radii, centeredPageStyle, authCardStyle } from "../styles/theme";

/**
 * #2316 — OAuth callback for Cognito hosted-UI federated sign-in.
 *
 * The hosted UI redirects here after a Google/GitHub login with the tokens in
 * the URL fragment (implicit flow):
 *   /auth/callback#id_token=...&access_token=...&expires_in=3600&token_type=Bearer
 * or, on error:
 *   /auth/callback#error=access_denied&error_description=...
 *
 * We parse the fragment, call auth.login(idToken, refreshToken), and route to
 * the board. Federated identities are pre-verified by the IdP, so there's no
 * email-verify step. Backend auto-provisions the 2hives-users row on first
 * /admin/me (the existing provisioning poll in AuthContext handles the wait).
 */
export function FleetAuthCallback() {
  const navigate = useNavigate();
  const brand = useBrand();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);

    const err = params.get("error");
    if (err) {
      setError(params.get("error_description") || err);
      return;
    }

    const idToken = params.get("id_token");
    if (!idToken) {
      setError("No sign-in token returned. Please try again.");
      return;
    }
    // Implicit flow does not return a refresh token; pass empty string.
    login(idToken, params.get("refresh_token") ?? "");
    // Clear the fragment so the tokens aren't left in history/URL.
    navigate("/fleet/board", { replace: true });
  }, [login, navigate]);

  return (
    <div style={centeredPageStyle}>
      <div style={authCardStyle}>
        <div style={{ textAlign: "center" }}>
          <img src={brand.logo_url} alt={brand.name} style={{ height: 56, marginBottom: "0.75rem" }} />
          {error ? (
            <>
              <h2 style={{ color: colors.error, fontSize: fontSizes.lg, fontWeight: 700, margin: "0 0 0.5rem" }}>
                Sign-in failed
              </h2>
              <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: "0 0 1.25rem" }}>
                {error}
              </p>
              <button
                onClick={() => navigate("/login", { replace: true })}
                style={{
                  background: "rgba(95,166,95,0.15)",
                  border: `1px solid ${colors.green}`,
                  color: colors.green,
                  padding: "0.5rem 1.2rem",
                  borderRadius: radii.sm,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: fontSizes.sm,
                }}
              >
                Back to sign in
              </button>
            </>
          ) : (
            <>
              <h2 style={{ color: brand.accent_color, fontSize: fontSizes.lg, fontWeight: 700, margin: "0 0 0.5rem" }}>
                Signing you in…
              </h2>
              <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: 0 }}>
                One moment while we finish setting up your session.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
